import React, { useEffect, useState } from 'react';
import api from '../api';
import { toDateTimeLocalValue } from '../utils/localDateTime';
import {
  Box, Typography, Card, CardContent, CardActions, Button, Grid, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Snackbar, Tooltip, Tabs, Tab, useTheme, useMediaQuery, Stack
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { Link as RouterLink } from 'react-router-dom';
import { isCommunicationsBridgeEnabled } from '../config/featureFlags';
import AddIcon from '@mui/icons-material/AddOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SendIcon from '@mui/icons-material/SendOutlined';
import MessageIcon from '@mui/icons-material/MessageOutlined';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import SearchIcon from '@mui/icons-material/Search';
import ReplayIcon from '@mui/icons-material/ReplayOutlined';
import ChatIcon from '@mui/icons-material/ChatOutlined';
import InputAdornment from '@mui/material/InputAdornment';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const MessageComposer = ({ propertyId, unitId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [templates, setTemplates] = useState([]);
  const [messages, setMessages] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [notificationSettings, setNotificationSettings] = useState({ emailRecipients: [] });
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [composeDialog, setComposeDialog] = useState(false);
  const [templateDialog, setTemplateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  
  // Form states
  const [composeData, setComposeData] = useState({
    recipients: [],
    subject: '',
    body: '',
    templateId: '',
    isScheduled: false,
    scheduledAt: ''
  });
  
  const [templateData, setTemplateData] = useState({
    name: '',
    subject: '',
    body: ''
  });
  
  const [editData, setEditData] = useState({
    id: '',
    subject: '',
    body: '',
    scheduledAt: ''
  });

  useEffect(() => {
    if (propertyId && unitId) {
      fetchData();
    }
  }, [propertyId, unitId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      setLoading(true);
      const [templatesRes, messagesRes, tenantsRes, contractorsRes, notificationSettingsRes] = await Promise.all([
        api.get('/api/templates'),
        api.get(`/api/properties/${propertyId}/units/${unitId}/messages`),
        api.get('/api/tenants'),
        api.get('/api/contractors'),
        api.get('/api/notification-settings')
      ]);
      
      // Normalize templates to ensure consistent structure
      const normalizedTemplates = templatesRes.data.map(template => {
        // If template has content but no body, use content as body
        if (template.content && !template.body) {
          return {
            ...template,
            body: template.content,
            subject: template.subject || template.name || ''
          };
        }
        // If template has body but no subject, use name as subject
        if (template.body && !template.subject) {
          return {
            ...template,
            subject: template.name || ''
          };
        }
        return template;
      });
      setTemplates(normalizedTemplates);
      const processedMessages = messagesRes.data.map(msg => ({ ...msg, status: msg.status || 'sent' }));
      console.log('📨 Fetched messages:', processedMessages);
      console.log('📨 Scheduled messages:', processedMessages.filter(m => m.status === 'scheduled'));
      setMessages(processedMessages);
      setTenants(tenantsRes.data);
      setContractors(contractorsRes.data);
      setNotificationSettings(notificationSettingsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setSnackbar({ open: true, message: 'Failed to fetch data', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleComposeMessage = async () => {
    try {
      const payload = {
        recipients: composeData.recipients, // Keep as array
        subject: composeData.subject,
        body: composeData.body,
        template: composeData.templateId || null,
        sentBy: 'admin',
      };

      if (composeData.isScheduled && composeData.scheduledAt) {
        payload.scheduledAt = new Date(composeData.scheduledAt).toISOString();
      }

      console.log('Sending message payload:', payload);
      const response = await api.post(`/api/properties/${propertyId}/units/${unitId}/messages`, payload);
      const isScheduled = composeData.isScheduled && composeData.scheduledAt;
      
      // Check email results if available
      const emailResults = response.data?.emailResults;
      let successMessage = isScheduled ? 'Message scheduled successfully!' : 'Message sent successfully!';
      
      if (emailResults && !isScheduled) {
        const successful = emailResults.filter(r => r.success).length;
        const failed = emailResults.filter(r => !r.success).length;
        if (successful > 0) {
          successMessage = `Message sent successfully! Email delivered to ${successful} recipient(s).`;
          if (failed > 0) {
            successMessage += ` ${failed} failed.`;
          }
        }
      }
      
      setSnackbar({ 
        open: true, 
        message: successMessage, 
        severity: 'success' 
      });
      setComposeDialog(false);
      resetComposeForm();
      fetchData();
    } catch (error) {
      console.error('Error sending message:', error);
      setSnackbar({ open: true, message: 'Failed to send message', severity: 'error' });
    }
  };


  const handleOpenTemplateDialog = () => {
    // Populate template data with current message content
    setTemplateData({
      name: '',
      subject: composeData.subject,
      body: composeData.body
    });
    setTemplateDialog(true);
  };

  const handleSaveTemplate = async () => {
    try {
      if (!templateData.name || !templateData.body) {
        setSnackbar({ open: true, message: 'Template name and body are required', severity: 'error' });
        return;
      }
      
      // Backend expects: name, category, content
      // For message templates, we'll store subject and body in a way that works with both structures
      const payload = {
        name: templateData.name,
        category: 'message', // Default category for message templates
        description: '', // Optional
        content: templateData.body, // Use body as content for backend compatibility
        // Also include subject and body for frontend compatibility when loading
        subject: templateData.subject || '',
        body: templateData.body
      };
      
      await api.post('/api/templates', payload);
      setSnackbar({ open: true, message: 'Template saved successfully!', severity: 'success' });
      setTemplateDialog(false);
      setTemplateData({ name: '', subject: '', body: '' });
      fetchData();
    } catch (error) {
      console.error('Error saving template:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to save template';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    try {
      await api.delete(`/api/properties/${propertyId}/units/${unitId}/messages/${messageId}`);
      setSnackbar({ open: true, message: 'Message deleted successfully!', severity: 'success' });
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to delete message', severity: 'error' });
    }
  };

  const handleEditMessage = async () => {
    try {
      await api.put(`/api/properties/${propertyId}/units/${unitId}/messages/${editData.id}`, {
        subject: editData.subject,
        body: editData.body,
        scheduledAt: editData.scheduledAt ? new Date(editData.scheduledAt).toISOString() : undefined,
      });
      setSnackbar({ open: true, message: 'Message updated successfully!', severity: 'success' });
      setEditDialog(false);
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to update message', severity: 'error' });
    }
  };

  const handleCancelScheduled = async (messageId) => {
    try {
      await api.delete(`/api/properties/${propertyId}/units/${unitId}/messages/${messageId}`);
      setSnackbar({ open: true, message: 'Scheduled message canceled!', severity: 'success' });
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to cancel message', severity: 'error' });
    }
  };

  const handleRetryMessage = async (messageId) => {
    try {
      await api.post(`/api/properties/${propertyId}/units/${unitId}/messages/${messageId}/retry`);
      setSnackbar({ open: true, message: 'Retry triggered!', severity: 'success' });
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to retry message', severity: 'error' });
    }
  };

  const handleProcessScheduled = async () => {
    try {
      const response = await api.post(`/api/properties/${propertyId}/units/${unitId}/messages/process-scheduled`);
      setSnackbar({ 
        open: true, 
        message: `Processed ${response.data.processedCount} scheduled messages!`, 
        severity: 'success' 
      });
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: 'Failed to process scheduled messages', severity: 'error' });
    }
  };

  const resetComposeForm = () => {
    setComposeData({
      recipients: [],
      subject: '',
      body: '',
      templateId: '',
      isScheduled: false,
      scheduledAt: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'sent': return 'primary';
      case 'delivered': return 'success';
      case 'failed': return 'error';
      case 'scheduled': return 'warning';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return <CheckCircleIcon color="success" />;
      case 'failed': return <ErrorIcon color="error" />;
      case 'scheduled': return <ScheduleIcon color="warning" />;
      default: return <SendIcon color="primary" />;
    }
  };

  const filteredMessages = messages.filter(msg => {
    const matchesStatus = statusFilter === 'all' || msg.status === statusFilter;
    const searchLower = search.toLowerCase();
    const matchesSearch = msg.subject?.toLowerCase().includes(searchLower) || 
                         msg.body?.toLowerCase().includes(searchLower);
    return matchesStatus && matchesSearch;
  });

  const tabMessages = activeTab === 0 ? filteredMessages :
                     activeTab === 1 ? messages.filter(m => m.status === 'scheduled') :
                     messages.filter(m => m.status === 'failed');

  if (!propertyId || !unitId) {
    return (
      <Box sx={{ mb: 4 }}>
        <Card sx={{ bgcolor: 'info.light', borderLeft: 4, borderColor: 'info.main' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <MessageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Compose New Message
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select a property and unit above to compose and send messages to tenants.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 2, sm: 3, md: 4 } }}><Typography>Loading messages...</Typography></Box>;
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {isCommunicationsBridgeEnabled() && (
        <Alert
          severity="info"
          icon={<ChatIcon fontSize="inherit" />}
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              component={RouterLink}
              to="/communications/new"
            >
              Open WhatsApp composer
            </Button>
          }
        >
          For WhatsApp / portal links / system templates, use Communications — not this email Messaging Center.
        </Alert>
      )}
      {/* Header Section */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 2, sm: 3 },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mb: { xs: 1, sm: 0 } }}
        >
          <MessageIcon sx={{ mr: { xs: 0.5, sm: 1 }, verticalAlign: 'middle' }} />
          Messaging Center
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          gap: { xs: 1, sm: 2 },
          flexDirection: { xs: 'column', sm: 'row' },
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleOpenTemplateDialog}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? 'Template' : 'Save Template'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleProcessScheduled}
            sx={{ minWidth: { xs: '100%', sm: 'auto' } }}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? 'Process' : 'Process Scheduled'}
          </Button>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setComposeDialog(true)}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? 'Compose' : 'Compose Message'}
          </Button>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs 
        value={activeTab} 
        onChange={(_, v) => setActiveTab(v)} 
        sx={{ mb: { xs: 2, sm: 3 } }}
        variant={isMobile ? 'scrollable' : 'standard'}
        scrollButtons="auto"
      >
        <Tab label="All Messages" />
        <Tab label="Scheduled" />
        <Tab label="Failed" />
      </Tabs>

      {/* Filter Controls */}
      <Box sx={{ 
        display: 'flex', 
        gap: { xs: 1.5, sm: 2 }, 
        mb: { xs: 2, sm: 3 }, 
        flexWrap: 'wrap',
        alignItems: 'center',
        flexDirection: { xs: 'column', sm: 'row' }
      }}>
        <TextField
          placeholder="Search messages..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          size={isMobile ? 'small' : 'medium'}
          sx={{ 
            minWidth: { xs: '100%', sm: 200 }, 
            flexGrow: { xs: 0, sm: 1 }, 
            maxWidth: { xs: '100%', sm: 400 },
            width: { xs: '100%', sm: 'auto' }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl 
          size={isMobile ? 'small' : 'medium'} 
          sx={{ 
            minWidth: { xs: '100%', sm: 140 },
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={e => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="sent">Sent</MenuItem>
            <MenuItem value="delivered">Delivered</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="scheduled">Scheduled</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Messages Grid */}
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
        {tabMessages.length === 0 ? (
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary">No messages found.</Typography>
            </Box>
          </Grid>
        ) : (
          tabMessages.map((message) => (
            <Grid item xs={12} md={6} lg={4} key={message.id}>
              <Card>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start', 
                    mb: { xs: 1.5, sm: 2 },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 0 }
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                      {getStatusIcon(message.status)}
                      <Typography 
                        variant="h6" 
                        noWrap 
                        sx={{ 
                          maxWidth: { xs: '100%', sm: 200 },
                          fontSize: { xs: '1rem', sm: '1.25rem' }
                        }}
                      >
                        {message.subject}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: { xs: 0.25, sm: 0.5 } }}>
                      <Tooltip title="Edit Message">
                        <IconButton 
                          size={isMobile ? 'small' : 'medium'} 
                          onClick={() => {
                            setEditData({
                              id: message.id,
                              subject: message.subject || '',
                              body: message.body || '',
                              scheduledAt: toDateTimeLocalValue(message.scheduledAt)
                            });
                            setEditDialog(true);
                          }}
                          aria-label="Edit message"
                        >
                          <EditIcon fontSize={isMobile ? 'small' : 'medium'} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Message">
                        <IconButton 
                          size={isMobile ? 'small' : 'medium'} 
                          color="error"
                          onClick={() => handleDeleteMessage(message.id)}
                          aria-label="Delete message"
                        >
                          <DeleteIcon fontSize={isMobile ? 'small' : 'medium'} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    gutterBottom
                    sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                    {message.body?.substring(0, 100)}...
                  </Typography>
                  
                  <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, mb: { xs: 1.5, sm: 2 }, flexWrap: 'wrap' }}>
                    <Chip 
                      label={message.status} 
                      color={getStatusColor(message.status)} 
                      size="small"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    />
                    <Chip 
                      label={`${message.recipients?.length || 1} recipients`} 
                      size="small" 
                      variant="outlined"
                      sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                    />
                  </Box>
                  
                  {/* Recipients Display */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                      Recipients:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {message.recipients && message.recipients.length > 0 ? (
                        message.recipients.map((recipient, index) => (
                          <Chip
                            key={index}
                            label={recipient}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        ))
                      ) : message.recipientId ? (
                        <Chip
                          label={message.recipientId}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No recipients specified
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Typography variant="caption" color="text.secondary">
                    {message.createdAt?._seconds 
                      ? new Date(message.createdAt._seconds * 1000).toLocaleString()
                      : message.createdAt 
                        ? new Date(message.createdAt).toLocaleString()
                        : 'Unknown date'
                    }
                  </Typography>
                </CardContent>
                
                <CardActions>
                  {message.status === 'scheduled' && (
                    <>
                      <Button 
                        size="small" 
                        onClick={() => handleCancelScheduled(message.id)}
                        color="error"
                      >
                        Cancel
                      </Button>
                    </>
                  )}
                  {message.status === 'failed' && (
                    <Button 
                      size="small" 
                      onClick={() => handleRetryMessage(message.id)}
                      color="warning"
                      startIcon={<ReplayIcon />}
                    >
                      Retry
                    </Button>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {/* Compose Message Dialog */}
      <Dialog 
        open={composeDialog} 
        onClose={() => setComposeDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Compose Message
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {isCommunicationsBridgeEnabled() && (
              <Alert severity="info">
                WhatsApp + portal-link templates live in{' '}
                <Button
                  component={RouterLink}
                  to="/communications/new"
                  size="small"
                  onClick={() => setComposeDialog(false)}
                  sx={{ verticalAlign: 'baseline', minWidth: 0, p: 0, textTransform: 'none' }}
                >
                  Communications → New Message
                </Button>
                .
              </Alert>
            )}
            <FormControl fullWidth>
              <InputLabel id="compose-recipients-label">Select Recipients</InputLabel>
              <Select
                labelId="compose-recipients-label"
                label="Select Recipients"
                multiple
                value={composeData.recipients}
                onChange={e => setComposeData({ ...composeData, recipients: e.target.value })}
                renderValue={(selected) => selected.join(', ')}
              >
                {tenants.length > 0 && (
                  <MenuItem disabled>
                    <Typography variant="subtitle2" color="primary">Tenants</Typography>
                  </MenuItem>
                )}
                {tenants.map(t => (
                  <MenuItem key={t.id} value={t.email}>
                    <Checkbox checked={composeData.recipients.indexOf(t.email) > -1} />
                    {t.firstName && t.lastName ? `${t.firstName} ${t.lastName}` : t.name} ({t.email})
                  </MenuItem>
                ))}
                {contractors.length > 0 && (
                  <MenuItem disabled>
                    <Typography variant="subtitle2" color="primary">Contractors</Typography>
                  </MenuItem>
                )}
                {contractors.map(c => (
                  <MenuItem key={c.id} value={c.email}>
                    <Checkbox checked={composeData.recipients.indexOf(c.email) > -1} />
                    {c.name} ({c.email}) - {c.specialties?.join(', ') || 'General'}
                  </MenuItem>
                ))}
                {notificationSettings.emailRecipients.length > 0 && (
                  <MenuItem disabled>
                    <Typography variant="subtitle2" color="primary">Test Recipients</Typography>
                  </MenuItem>
                )}
                {notificationSettings.emailRecipients.map(recipient => (
                  <MenuItem key={recipient.id} value={recipient.email}>
                    <Checkbox checked={composeData.recipients.indexOf(recipient.email) > -1} />
                    {recipient.name} ({recipient.email}) - Test
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="compose-template-label">Select Template</InputLabel>
              <Select
                labelId="compose-template-label"
                label="Select Template"
                value={composeData.templateId}
                onChange={e => {
                  const template = templates.find(t => t.id === e.target.value);
                  if (template) {
                    const subject = template.subject || '';
                    const body = template.body || template.content || '';
                    setComposeData({
                      ...composeData,
                      templateId: e.target.value,
                      subject: subject,
                      body: body
                    });
                  } else {
                    setComposeData({
                      ...composeData,
                      templateId: '',
                      subject: '',
                      body: ''
                    });
                  }
                }}
              >
                <MenuItem value="">No Template</MenuItem>
                {templates.map(template => (
                  <MenuItem key={template.id} value={template.id}>{template.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Subject"
              value={composeData.subject}
              onChange={e => setComposeData({ ...composeData, subject: e.target.value })}
              fullWidth
            />
            <TextField
              label="Message Body"
              value={composeData.body}
              onChange={e => setComposeData({ ...composeData, body: e.target.value })}
              fullWidth
              multiline
              rows={6}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={composeData.isScheduled}
                  onChange={e => setComposeData({ ...composeData, isScheduled: e.target.checked })}
                />
              }
              label="Schedule for Later"
            />
            {composeData.isScheduled && (
              <TextField
                label="Scheduled At"
                type="datetime-local"
                value={composeData.scheduledAt}
                onChange={e => setComposeData({ ...composeData, scheduledAt: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setComposeDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleComposeMessage} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {isMobile ? 'Send' : 'Send Message'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog 
        open={templateDialog} 
        onClose={() => setTemplateDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Save Template
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Template Name"
              value={templateData.name}
              onChange={e => setTemplateData({ ...templateData, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Subject"
              value={templateData.subject}
              onChange={e => setTemplateData({ ...templateData, subject: e.target.value })}
              fullWidth
            />
            <TextField
              label="Message Body"
              value={templateData.body}
              onChange={e => setTemplateData({ ...templateData, body: e.target.value })}
              fullWidth
              multiline
              rows={6}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setTemplateDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveTemplate} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {isMobile ? 'Save' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Message Dialog */}
      <Dialog 
        open={editDialog} 
        onClose={() => setEditDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
          Edit Scheduled Message
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mt: { xs: 1, sm: 1 } }}>
            <Grid item xs={12}>
              <TextField
                label="Subject"
                value={editData.subject}
                onChange={e => setEditData({ ...editData, subject: e.target.value })}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Message Body"
                value={editData.body}
                onChange={e => setEditData({ ...editData, body: e.target.value })}
                fullWidth
                margin="normal"
                multiline
                rows={6}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Scheduled At"
                type="datetime-local"
                value={editData.scheduledAt}
                onChange={e => setEditData({ ...editData, scheduledAt: e.target.value })}
                fullWidth
                InputLabelProps={{ shrink: true }}
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: { xs: 1, sm: 0 } }}>
          <Button 
            onClick={() => setEditDialog(false)}
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleEditMessage} 
            variant="contained"
            fullWidth={isMobile}
            size={isMobile ? 'medium' : 'medium'}
          >
            {isMobile ? 'Update' : 'Update Message'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MessageComposer;
